import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { currencies as allCurrencies, defaults, display, getCurrencyConfig, widgetConfig } from '../config/config';
import { Currency } from '../types/currency';
import { ChanceToMatch, LoanType } from '../types/loanType';
import { calculateOptimalRate } from '../utils/calculateOptimalRate';
import { checkInstantAvailability } from '../utils/checkInstantAvailability';
import { parseCurrencyOptions, resolveInitialCurrency } from '../utils/urlParams';
import { useChanceToMatch } from './useChanceToMatch';

type Step = 1 | 2 | 3;
type Direction = 'forward' | 'back';

interface UseLoanRequestFormProps {
  currency?: string;
  currencyOptions?: string;
  partner?: string;
  email?: string;
  forwardParams?: Record<string, string>;
  locale?: string;
  amount?: string;
}

export function useLoanRequestForm({ currency: currencyParam, currencyOptions: currencyOptionsParam, partner, email: emailParam, forwardParams, locale, amount: amountParam }: UseLoanRequestFormProps) {
  const intl = useIntl();
  const { computeChanceToMatch } = useChanceToMatch();

  const availableCurrencies = parseCurrencyOptions(currencyOptionsParam, allCurrencies) ?? allCurrencies;
  const initialCurrency = resolveInitialCurrency(currencyParam, availableCurrencies, defaults.currency);
  const initialAmount = (amountParam ?? '').replace(/\D/g, '').slice(0, display.maxFiatDigits);

  const [step, setStep] = useState<Step>(1);
  const [prevStep, setPrevStep] = useState<Step | null>(null);
  const [direction, setDirection] = useState<Direction>('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [amount, setAmount] = useState<string>(initialAmount);
  const [period, setPeriod] = useState<number>(defaults.period);
  const [error, setError] = useState<string | undefined>();
  const [errorKey, setErrorKey] = useState<number>(0);
  const [loanType, setLoanType] = useState<LoanType>(LoanType.None);
  const [customInterestRate, setCustomInterestRate] = useState<string>('8');
  const [email, setEmail] = useState<string>(emailParam ?? '');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [emailErrorKey, setEmailErrorKey] = useState<number>(0);

  const goToStep = (newStep: Step) => {
    const newDirection = newStep > step ? 'forward' : 'back';
    setDirection(newDirection);
    setPrevStep(step);
    setStep(newStep);
    setIsAnimating(true);
  };

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setPrevStep(null);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  const handleAmountChange = (value: string) => {
    setAmount(value);
    if (error) {
      setError(undefined);
    }
  };

  const handleCurrencyChange = (value: Currency) => {
    setCurrency(value);
    if (error) {
      setError(undefined);
    }
  };

  const handleSubmit = () => {
    const currencyConfig = getCurrencyConfig(currency);
    const minAmount = currencyConfig
      ? parseFloat(currencyConfig.minTransactionLimit)
      : 0;

    const numericAmount = parseFloat(amount) || 0;

    if (numericAmount < minAmount) {
      const formattedMin = intl.formatNumber(Math.round(minAmount), { maximumFractionDigits: 0 });
      setError(
        intl.formatMessage(
          { id: 'form.amount.error.min' },
          { min: `${currency} ${formattedMin}` }
        )
      );
      setErrorKey(prev => prev + 1);
      return;
    }

    // Auto-select loan type: Instant if available, otherwise Custom
    if (currencyConfig) {
      const ticket = currencyConfig.investTickets.find(t => t.period === period);
      const availablePeriods = currencyConfig.investTickets.map(t => t.period);

      const instantAvailability = checkInstantAvailability({
        instantRate: ticket?.interestRate ?? null,
        amount,
        maxInstantTransactionLimit: currencyConfig.maxInstantTransactionLimit,
        availableTicketAmount: ticket?.amount ?? '0',
        availableInstantPeriods: availablePeriods,
      });

      setLoanType(instantAvailability.available ? LoanType.Instant : LoanType.Custom);

      // Calculate optimal interest rate for Custom loan type
      const minRate = parseFloat(currencyConfig.minInterestRate);
      const maxRate = parseFloat(currencyConfig.maxInterestRate);
      const availableInstantRate = instantAvailability.available
        ? ticket?.interestRate
        : undefined;

      const optimalRate = calculateOptimalRate({
        amount,
        period,
        currency,
        minRate,
        maxRate,
        availableInstantRate,
        computeChanceToMatch,
      });

      setCustomInterestRate(optimalRate.toString());
    } else {
      setLoanType(LoanType.Custom);
    }

    goToStep(2);
  };

  const handleBack = () => {
    if (step === 3) {
      setEmailError(undefined);
      goToStep(2);
    } else {
      setLoanType(LoanType.None);
      goToStep(1);
    }
  };

  const handleLoanTypeContinue = () => {
    goToStep(3);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError(undefined);
    }
  };

  const isValidEmail = (emailValue: string): boolean => {
    if (!emailValue.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue.trim());
  };

  const buildSubmitUrl = () => {
    const currencyConfig = getCurrencyConfig(currency);
    const ticket = currencyConfig?.investTickets.find(t => t.period === period);

    const isInstant = loanType === LoanType.Instant;
    const rate = isInstant ? ticket?.interestRate : customInterestRate;

    const chanceToMatch = computeChanceToMatch({
      amount: parseFloat(amount),
      interestRate: parseFloat(rate || '0'),
      period,
      currency,
    });

    const chanceToMatchMap: Record<ChanceToMatch, string> = {
      [ChanceToMatch.LOW]: 'low',
      [ChanceToMatch.MEDIUM]: 'medium',
      [ChanceToMatch.HIGH]: 'high',
      [ChanceToMatch.VERY_HIGH]: 'very_high',
      [ChanceToMatch.NA]: 'na',
    };
    const chanceToMatchStr = chanceToMatch ? chanceToMatchMap[chanceToMatch] : 'na';

    const typeMap: Record<LoanType, string> = {
      [LoanType.Instant]: 'instant',
      [LoanType.Custom]: 'custom',
      [LoanType.Otc]: 'otc',
      [LoanType.None]: 'none',
    };

    const params = new URLSearchParams({
      currency: currency,
      amount: amount,
      period: period.toString(),
      type: typeMap[loanType],
      rate: rate || '',
      chance_to_match: chanceToMatchStr,
      btc_price: currencyConfig?.bitcoinPrice || '',
      origination_fee_rate: widgetConfig.originationFeeRate,
      email: email,
    });

    if (partner) {
      params.set('partner', partner);
    }

    if (locale) {
      params.set('locale', locale);
    }

    if (forwardParams) {
      for (const [key, value] of Object.entries(forwardParams)) {
        params.set(key, value);
      }
    }

    return `https://app.firefish.io/borrow/tab/summary?${params.toString()}`;
  };

  const handleEmailContinue = () => {
    if (!isValidEmail(email)) {
      setEmailError(intl.formatMessage({ id: 'email.error.invalid' }));
      setEmailErrorKey(prev => prev + 1);
      return;
    }

    const url = buildSubmitUrl();
    window.open(url, '_blank');
  };

  const handleCustomInterestRateChange = (rate: string | number) => {
    setCustomInterestRate(String(rate));
  };

  const getCtaText = () => {
    if (step === 1) return intl.formatMessage({ id: 'form.submit' });
    return intl.formatMessage({ id: 'button.continue' });
  };

  const getCtaHandler = (): ((e?: React.MouseEvent) => void) => {
    if (step === 1) return handleSubmit;
    if (step === 2) return handleLoanTypeContinue;
    return handleEmailContinue;
  };

  const isCtaDisabled = step === 2 && loanType === LoanType.None;

  return {
    step,
    prevStep,
    direction,
    isAnimating,
    currency,
    amount,
    period,
    error,
    errorKey,
    loanType,
    customInterestRate,
    email,
    emailError,
    emailErrorKey,
    availableCurrencies,
    setPeriod,
    setLoanType,
    handleAmountChange,
    handleCurrencyChange,
    handleSubmit,
    handleBack,
    handleLoanTypeContinue,
    handleEmailChange,
    handleEmailContinue,
    handleCustomInterestRateChange,
    getCtaText,
    getCtaHandler,
    isCtaDisabled,
  };
}
